/**
 * Enrollment submission: builds the payload from enroll state and posts it to the public
 * tRPC endpoint. Callers depend only on the EnrollmentSubmission -> EnrollmentConfirmation contract.
 */
import type {CoverageTier, GroupConfig, ProductCategory} from '@client/pages/public/enroll/types';
import {COVERAGE_TIER_LABELS} from '@client/pages/public/enroll/types';
import type {
  Applicant,
  Beneficiary,
  Dependent,
  EnrollSelections,
  EnrollTiers,
} from '@client/pages/public/enroll/enrollModel';
import {getPlan, selectionTotal, tierFor} from '@client/pages/public/enroll/enrollModel';
import {api} from '@common-web/lib/trpc';
import {getEnrollAccess} from '@client/pages/public/enroll/enrollAccess';

/** One priced line on the confirmation receipt (a chosen plan only — declines are omitted). */
export interface EnrollmentElection {
  category: ProductCategory;
  planId: string;
  productLabel: string;
  planName: string;
  tier: CoverageTier;
  /** Null when the product has no tier picker (e.g. employee-only STD). */
  tierLabel: string | null;
  network: string | null;
  weeklyCost: number;
}

/** The request payload sent to the server (what the user elected + who they are). */
export interface EnrollmentSubmission {
  groupSlug: string;
  groupName: string;
  subscriber: Applicant;
  dependents: Dependent[];
  beneficiary: Beneficiary | null;
  elections: EnrollmentElection[];
  totalWeekly: number;
}

/** The server's response: the submission echoed back plus the authoritative receipt fields. */
export interface EnrollmentConfirmation extends EnrollmentSubmission {
  confirmationNumber: string;
  /** ISO timestamp the enrollment was recorded. */
  submittedAt: string;
}

/** Derive the submission payload from the current enroll state. Pure — safe to call on submit. */
export function buildEnrollmentSubmission(
  group: GroupConfig,
  tiers: EnrollTiers,
  selections: EnrollSelections,
  subscriber: Applicant,
  dependents: Dependent[],
  beneficiary: Beneficiary | null,
): EnrollmentSubmission {
  const elections: EnrollmentElection[] = [];
  for (const product of group.products) {
    const selection = selections[product.category];
    if (!selection?.planId) continue;
    const plan = getPlan(group, selection.planId);
    if (!plan) continue;
    const tier = tierFor(tiers, product.category);
    const showTier = product.tierSelectable !== false;
    elections.push({
      category: product.category,
      planId: plan.id,
      productLabel: product.label,
      planName: plan.name,
      tier,
      tierLabel: showTier ? COVERAGE_TIER_LABELS[tier] : null,
      network: plan.network ?? null,
      weeklyCost: plan.rates[tier],
    });
  }

  return {
    groupSlug: group.slug,
    groupName: group.name,
    subscriber,
    dependents,
    beneficiary,
    elections,
    totalWeekly: selectionTotal(group, tiers, selections),
  };
}

/** Thrown when e123 already has a current-plan-year enrollment for this person. */
export class AlreadyEnrolledError extends Error {}

/** Thrown when the location access code is missing (session expired) — user must re-enter it. */
export class AccessExpiredError extends Error {}

export async function submitEnrollment(
  submission: EnrollmentSubmission,
  options?: {preview?: boolean; demo?: boolean; requiresAccessCode?: boolean},
): Promise<EnrollmentConfirmation> {
  // Preview and demo never post to e123 — return a sample confirmation from the same contract.
  if (options?.preview || options?.demo) {
    const confirmationNumber = options?.demo ? 'DEMO' : 'PREVIEW';
    return {...submission, confirmationNumber, submittedAt: new Date().toISOString()};
  }
  // Policy comes from the caller's resolved (DB) config; default to requiring a code if unknown.
  const requiresCode = options?.requiresAccessCode ?? true;
  const access = getEnrollAccess(submission.groupSlug);
  if (requiresCode && !access) {
    throw new AccessExpiredError();
  }
  let result;
  try {
    result = await api.enrollment.submitEnrollment.mutate({
    groupSlug: submission.groupSlug,
    accessCode: access?.accessCode,
    subscriber: {
      firstName: submission.subscriber.firstName,
      lastName: submission.subscriber.lastName,
      dob: submission.subscriber.dob,
      gender: submission.subscriber.gender as 'M' | 'F',
      ssn: submission.subscriber.ssn,
      email: submission.subscriber.email,
      phone: submission.subscriber.phone,
      address1: submission.subscriber.address1,
      address2: submission.subscriber.address2,
      city: submission.subscriber.city,
      state: submission.subscriber.state,
      zip: submission.subscriber.zip,
    },
    dependents: submission.dependents.map((dep) => ({
      firstName: dep.firstName,
      lastName: dep.lastName,
      relationship: dep.relationship,
      dob: dep.dob,
      gender: dep.gender as 'M' | 'F',
      ssn: dep.ssn,
    })),
    beneficiary: submission.beneficiary ?? undefined,
    elections: submission.elections.map((election) => ({
      category: election.category,
      planId: election.planId,
      tier: election.tier,
    })),
  });
  } catch (err) {
    if ((err as {data?: {code?: string}} | null)?.data?.code === 'CONFLICT') {
      throw new AlreadyEnrolledError();
    }
    throw err;
  }
  return {...submission, confirmationNumber: result.confirmationNumber, submittedAt: result.submittedAt};
}
